import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload, faSpinner, faPenToSquare, faImage, faVideo, faStar, faCircleCheck,
  faCircleXmark,
  faPhoneSlash,
  faCircleQuestion
} from '@fortawesome/free-solid-svg-icons';
import { ALL_COLUMNS } from '../../utils/placedtableConstants';
import { formatValue } from '../../utils/excelUtils';
import { useNavigate } from "react-router-dom";
import PlacementDetailsModal from './PlacementDetailsModal';
import { useToast } from '../Admissionform/ToastManager';

const DataTable = ({
  currentItems,
  selectedColumns,
  indexOfFirstItem,
  selectedStudents = new Set(),
  onStudentSelection,
  onSelectAll,
}) => {
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [downloadingStates, setDownloadingStates] = useState({});
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);
  const { showWarning, showError, showSuccess } = useToast();
  const navigate = useNavigate();


  const percentFields = [
    'project1Score',
    'project2Score',
    'project3Score',
    'communicationScore',
    'technicalScore',
    'mockTest1Score',
    'mockTest2Score',
    'mockTest3Score'
  ];


  const handleDownloadClick = async (rowIndex) => {
    const student = currentItems[rowIndex];
    const studentId = student.studentId;

    if (!studentId) {
      showError('Student ID not found. Cannot download resume.');
      return;
    }

    setDownloadingStates(prev => ({
      ...prev,
      [rowIndex]: true
    }));

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/download/${studentId}`, {
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error(`Failed to download resume: ${response.status} ${response.statusText}`);
      }

      const contentType = response.headers.get('Content-Type');

      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        if (data.hasResume === false) {
          showWarning(data.message || 'No resume found for this student.');
        } else {
          showError(data.message || 'Unexpected response from server.');
        }
        return;
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `${student.studentName || 'student'}_${student.Department}_${student.branch}_resume.pdf`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log(`Resume downloaded successfully for ${student.studentName}`);
    } catch (error) {
      console.error('Error downloading resume:', error);
      showError(`Failed to download resume: ${error.message}`);
    } finally {
      setDownloadingStates(prev => ({
        ...prev,
        [rowIndex]: false
      }));
    }
  };

  // Handle individual student selection
  const handleStudentCheck = (studentId, isChecked) => {
    if (onStudentSelection) {
      onStudentSelection(studentId, isChecked);
    }
  };

  // Handle select all checkbox
  const handleSelectAllCheck = (isChecked) => {
    if (onSelectAll) {
      onSelectAll(isChecked);
    }
  };

  // Check if all current page students are selected
  const isAllCurrentPageSelected = () => {
    return currentItems.length > 0 && currentItems.every(student =>
      selectedStudents.has(student.studentId)
    );
  };

  // Check if some current page students are selected
  const isSomeCurrentPageSelected = () => {
    return currentItems.some(student => selectedStudents.has(student.studentId));
  };

  // Handle bulk download
  const handleBulkDownload = async () => {
    if (selectedStudents.size === 0) {
      showError('Please select at least one student to download resumes.');
      return;
    }

    setIsBulkDownloading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/resumes/download-bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          studentIds: Array.from(selectedStudents)
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to download resumes');
      }

      // Handle zip file download
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const currentDate = new Date().toISOString().split('T')[0];
      link.download = `Selected_Resumes_${currentDate}.zip`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showSuccess(`Successfully downloaded ${selectedStudents.size} resume(s)!`);

      // Optionally clear selection after download
      if (onSelectAll) {
        onSelectAll(false);
      }

    } catch (error) {
      console.error('Error downloading bulk resumes:', error);
      showError(`Failed to download resumes: ${error.message}`);
    } finally {
      setIsBulkDownloading(false);
    }
  };

  const closeModal = () => {
    setShowViewModal(false);
    setSelectedRecord(null);
  };

  return (
    <>
      {/* Bulk Actions Bar */}
      {selectedStudents.size > 0 && (
        <div className="bulk-actions-bar">
          <div className="selected-count">
            {selectedStudents.size} student(s) selected
          </div>
          <div className="bulk-actions">
            <button
              onClick={handleBulkDownload}
              className="bulk-download-btn"
              disabled={isBulkDownloading}
              title="Download selected resumes as ZIP"
            >
              {isBulkDownloading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
                  Downloading...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faDownload} />
                  Download Selected ({selectedStudents.size})
                </>
              )}
            </button>
          </div>
        </div>
      )}

      <div className="table-container">
        <table className="records-table">
          <thead>
            <tr>
              <th className="select-column">
                <input
                  type="checkbox"
                  checked={isAllCurrentPageSelected()}
                  ref={input => {
                    if (input) {
                      input.indeterminate = isSomeCurrentPageSelected() && !isAllCurrentPageSelected();
                    }
                  }}
                  onChange={(e) => handleSelectAllCheck(e.target.checked)}
                  title="Select all students on this page"
                />
              </th>
              {ALL_COLUMNS
                .filter(column => selectedColumns.includes(column.key))
                .map(column => (
                  <th key={column.key}>{column.label}</th>
                ))}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.length === 0 ? (
              <tr>
                <td colSpan={selectedColumns.length + 2} style={{ textAlign: 'center', padding: '20px' }}>
                  No records found
                </td>
              </tr>
            ) : (
              currentItems.map((item, rowIndex) => {
                // ✅ Safe check for resumePath
                const resumePath = item?.resumePath || item?.studentRegistration?.resumePath;
                const hasResume = !!resumePath;

                return (
                  <tr
                    key={`${item.id || item.studentName || 'row'}-${indexOfFirstItem + rowIndex}`}
                    className={`
                        ${resumePath == null || resumePath === "" ? "no-resume-row" : ""}
            ${item.ProgressStatus === "In Active" ? "inactive-row" : ""}
            ${selectedStudents.has(item.studentId) ? "selected-row" : ""}
          `}
                  >
                    <td className="select-column">
                      <input
                        type="checkbox"
                        checked={selectedStudents.has(item.studentId)}
                        onChange={(e) => handleStudentCheck(item.studentId, e.target.checked)}
                        title="Select this student"
                      />
                    </td>

                    {ALL_COLUMNS
                      .filter(column => selectedColumns.includes(column.key))
                      .map(column => (
                        <td
                          key={column.key}
                          className="table-cell"
                          title={formatValue(item[column.key])}  // <-- Tooltip on hover
                        >
                          {
                            column.key === "branchDepartment"
                              ? `${item.branch || ""}${item.Department ? ", " + item.Department : ""}`
                              : column.key === "reviewStatus"
                                ? (
                                  <div className="review-icons">
                                    {/* PHOTO REVIEW */}
                                    {(item.photoReview === "Yes" || item.photoReview === "No") && (
                                      <span
                                        title={
                                          item.photoReview === "No" && item.photoReviewReason
                                            ? `Photo Review: ${item.photoReviewReason}`
                                            : `Photo Review: ${item.photoReview}`
                                        }
                                        style={{ cursor: 'pointer' }}
                                      >
                                        <FontAwesomeIcon
                                          icon={faImage}
                                          className={`review-icon ${item.photoReview === "Yes" ? "photo" : "grey"}`}
                                        />
                                      </span>
                                    )}

                                    {/* VIDEO REVIEW */}
                                    {(item.videoReview === "Yes" || item.videoReview === "No") && (
                                      <span
                                        title={
                                          item.videoReview === "No" && item.videoReviewReason
                                            ? `Video Review:${item.videoReviewReason}`
                                            : `Video Review: ${item.videoReview}`
                                        }
                                        style={{ cursor: 'pointer' }}
                                      >
                                        <FontAwesomeIcon
                                          icon={faVideo}
                                          className={`review-icon ${item.videoReview === "Yes" ? "video" : "grey"}`}
                                        />
                                      </span>
                                    )}

                                    {/* GOOGLE REVIEW */}
                                    {(item.googleReview === "Yes" || item.googleReview === "No") && (
                                      <span
                                        title={
                                          item.googleReview === "No" && item.googleReviewReason
                                            ? `Google Review: ${item.googleReviewReason}`
                                            : item.googleReview === "Yes" && item.googleReviewStars
                                              ? `Google Review: ${item.googleReview} (${item.googleReviewStars} stars)`
                                              : `Google Review: ${item.googleReview}`
                                        }
                                        style={{ cursor: 'pointer' }}
                                      >
                                        <FontAwesomeIcon
                                          icon={faStar}
                                          className={`review-icon ${item.googleReview === "Yes" ? "google" : "grey"}`}
                                        />
                                      </span>
                                    )}
                                    {/* VERIFICATION STATUS */}
                                    {item.joiningStatus && (
                                      <span
                                        title={
                                          item.verificationRemarks
                                            ? `${item.joiningStatus}: ${item.verificationRemarks}`
                                            : item.joiningStatus
                                        }
                                        style={{ cursor: "pointer" }}
                                      >
                                        <FontAwesomeIcon
                                          icon={
                                            item.joiningStatus === "Verified"
                                              ? faCircleCheck
                                              : item.joiningStatus === "Incorrect Data"
                                                ? faCircleXmark
                                                : item.joiningStatus === "Not Responding"
                                                  ? faPhoneSlash
                                                  : faCircleQuestion
                                          }
                                          className={`review-icon ${item.joiningStatus === "Verified"
                                              ? "verified"
                                              : item.joiningStatus === "Incorrect Data"
                                                ? "incorrect"
                                                : item.joiningStatus === "Not Responding"
                                                  ? "not-responding"
                                                  : "grey"
                                            }`}
                                        />
                                      </span>
                                    )}
                                  </div>

                                )
                                : column.key === "qualification"
                                  ? [item.ugDegree, item.pgDegree].filter(Boolean).join(", ")

                                  : column.key === "passedYear"
                                    ? [item.ugPassout, item.pgPassout].filter(Boolean).join(", ")

                                    : percentFields.includes(column.key)
                                      ? item[column.key] != null
                                        ? `${item[column.key]}%`
                                        : "N/A"
                                      : column.key === "studentName"
                                        ? (
                                          <div>
                                            <div style={{ fontWeight: "600" }}>
                                              {item.studentName}
                                            </div>

                                            <div
                                              style={{
                                                fontSize: "11px",
                                                color: "#0d6efd",
                                                marginTop: "2px"
                                              }}
                                            >
                                              {item.placementStatus}
                                            </div>
                                          </div>
                                        )
                                        : formatValue(item[column.key])
                          }
                        </td>
                      ))}

                    <td className="action-buttons">
                      <button
                        onClick={() => navigate(`/edits-student/${item.id}`)}
                        className="edit-btn"
                        title="Edit Record"
                      >
                        <FontAwesomeIcon icon={faPenToSquare} />
                      </button>
                      {resumePath && (
                        <button
                          onClick={() => handleDownloadClick(rowIndex)}
                          className="editpp-btn"
                          title="Download Resume"
                        >
                          <FontAwesomeIcon icon={faDownload} />
                        </button>
                      )}
                    </td>

                  </tr>
                );
              })
            )}
          </tbody>

        </table>
      </div>
      {/* Placement Details Modal */}
      <PlacementDetailsModal
        isOpen={showViewModal}
        onClose={closeModal}
        selectedRecord={selectedRecord}
      />
    </>
  );
};

export default DataTable;